import { ProjectEditSessionService } from '../../core/services/project-edit-session.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { GenerateRefreshToken, Login, Logout } from './auth.actions';
import { Injectable, Injector } from '@angular/core';
import { AuthService as OAuthAuthService } from '../../components/auth/auth.service';
import { of } from 'rxjs';

/**
 * User model for NGXS state
 */
export interface User {
  username?: string;
  name?: string;
  acct?: string;
  email?: string;
  user_loginid?: string;
  identityAliases?: string[];
}

export class AuthStateModel {
  accessToken: string | undefined;
  idToken: string | undefined;
  user: User | null = null;
}

/**
 * NGXS Auth State
 * 
 * Tokens are managed by angular-oauth2-oidc library.
 * This state provides selectors for backward compatibility with existing components.
 */
@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    accessToken: undefined,
    idToken: undefined,
    user: null
  },
})
@Injectable()
export class AuthState {
  private _oauthService: OAuthAuthService | null = null;

  constructor(private injector: Injector) {}

  /**
   * Lazy-load OAuth service to avoid circular dependency
   */
  private get oauthService(): OAuthAuthService {
    if (!this._oauthService) {
      this._oauthService = this.injector.get(OAuthAuthService);
    }
    return this._oauthService;
  }

  @Selector()
  static getAuthState(state: AuthStateModel): AuthStateModel {
    return state;
  }

  @Selector()
  static getAccessToken(state: AuthStateModel): string | undefined {
    return state?.accessToken;
  }

  @Selector()
  static getIdToken(state: AuthStateModel): string | undefined {
    return state?.idToken;
  }

  @Selector()
  static getLoggedInUserProfile(state: AuthStateModel): User | null | undefined {
    return state.user;
  }

  @Selector()
  static getLoggedInUserName(state: AuthStateModel): string | undefined {
    const user = state?.user;
    return user?.username ?? user?.name ?? user?.email ?? user?.user_loginid;
  }

  @Selector()
  static isTokenExpired(state: AuthStateModel): boolean {
    return !state?.accessToken;
  }

  /**
   * Login action - syncs NGXS state with OAuth tokens
   */
  @Action(Login)
  login(ctx: StateContext<AuthStateModel>, action: Login) {
    if (this.oauthService.hasValidAccessToken) {
      const profile: any = this.oauthService.profile$.value;

      const user: User = {
        username: profile?.email || profile?.user_loginid || '',
        name: profile?.name || '',
        email: profile?.email || '',
        acct: profile?.email || profile?.user_loginid || '',
        user_loginid: profile?.user_loginid || '',
        identityAliases: [profile?.email, profile?.acct, profile?.user_loginid, profile?.preferred_username].filter((value): value is string => typeof value === 'string' && !!value)
      };

      ctx.setState({
        accessToken: this.oauthService.accessToken || undefined,
        idToken: this.oauthService.idToken || undefined,
        user: user
      });
    }
    
    return of(null);
  }

  /**
   * Logout action - delegates to OAuth service
   */
  @Action(Logout)
  async logout(ctx: StateContext<AuthStateModel>, action: Logout) {
    try {
      await this.injector.get(ProjectEditSessionService).beforeLogout();
    } catch { /* Logout must still complete if recovery is unavailable. */ }
    try {
      this.oauthService.logout();
    } catch (error) {
      console.error('Logout service failed:', error);
    }
    
    ctx.setState({
      accessToken: undefined,
      idToken: undefined,
      user: null
    });
  }

  /**
   * Refresh token action - delegates to OAuth service
   */
  @Action(GenerateRefreshToken)
  async generateRefreshToken(ctx: StateContext<AuthStateModel>) {
    try {
      await this.oauthService.refreshToken();
      
      if (this.oauthService.hasValidAccessToken) {
        const profile: any = this.oauthService.profile$.value;

        const user: User = {
          username: profile?.email || profile?.user_loginid || '',
          name: profile?.name || '',
          email: profile?.email || '',
          acct: profile?.email || profile?.user_loginid || '',
          user_loginid: profile?.user_loginid || '',
          identityAliases: [profile?.email, profile?.acct, profile?.user_loginid, profile?.preferred_username].filter((value): value is string => typeof value === 'string' && !!value)
        };

        ctx.setState({
          accessToken: this.oauthService.accessToken || undefined,
          idToken: this.oauthService.idToken || undefined,
          user: user
        });
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      ctx.setState({
        accessToken: undefined,
        idToken: undefined,
        user: null
      });
      throw error;
    }
  }
}
